import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { Button as PriButton } from "@mui/material"
import { openInvoices, selectActivePage, selectLGInternalUser } from "../../activeScreenSlice"
import UnassignModal from "../UnassignModal/UnassignModal"
import { useState } from "react"
import RejectModal from "../RejectModal/RejectModal"
import MoveInvoiceModal from "../MoveInvoiceModal/MoveInvoiceModal"
import { SaveInvoiceArgs } from "../../service"
import { useInvoiceSave, useInvoiceSkipValidation } from "../../hooks"
import useShowToast from "../../hooks/useShowToast"
import useShowError from "../../hooks/useShowError"
import {
  AssignedToLabel,
  AssignedToName,
  AssignedToSlugs,
  Bar,
  Separator,
  Spacer,
} from "./InvoiceBar.styles"
import SkipValidationModal from "../SkipValidationModal/SkipValidationModal"
import { breakStringIntoLines } from "../../util/breakString"
import { convertDateToYyyyMmDd } from "../../util/formatDate"
import { ArrowBackOutlined } from '@mui/icons-material';

export default function InvoiceBar({
  enableSaveAndSubmit,
}: {
  enableSaveAndSubmit: boolean
}) {
  const dispatch = useAppDispatch()
  const LGInternalUser = useAppSelector(selectLGInternalUser);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false)
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [skipValidationModalOpen, setSkipValidationModalOpen] = useState(false)
  const invoiceId = useAppSelector(selectActivePage).selectedInvoice
  const enteredInvoiceInformation =
    useAppSelector(selectActivePage).enteredInvoiceInformation
  const saveInvoiceMutation = useInvoiceSave()
  const skipValidationMutation = useInvoiceSkipValidation()
  const showToast = useShowToast()
  const showError = useShowError()

  function handleSkipValidation() {
    const userFlagRaw = enteredInvoiceInformation.invoice_user_flag
    if (!userFlagRaw) {
      showError("User Flag is required before skipping validation.")
      return
    }
    const saveDetails = buildSaveObject("skipValidations")
  
    skipValidationMutation.mutate(saveDetails, {
      onSuccess: (res) => {
        showToast("Invoice successfully submitted and will skip validation checks")
        dispatch(openInvoices())
      },
      onError: (err) => {
        showError(
          "An error occurred while attempting to submit this invoice: " +
          err.name +
          " " +
          err.message,
        )
      },
    })
  }
  
  function handleSave() {
    const saveDetails = buildSaveObject("save")

    saveInvoiceMutation.mutate(saveDetails, {
      onSuccess: (res) => {
        showToast("Invoice successfully saved")
      },
      onError: (err) => {
        showError(
          "An error occurred while attempting to save this invoice: " +
          err.name +
          " " +
          err.message,
        )
      },
    })
  }
 
  function handleSubmit() {
    const userFlagRaw = enteredInvoiceInformation.invoice_user_flag
    if (!userFlagRaw) {
      showError("User Flag is required before submitting.")
      return
    }
    const saveDetails = buildSaveObject("submit")
    saveInvoiceMutation.mutate(saveDetails, {
      onSuccess: (res) => {
        showToast("Invoice successfully submitted for processing")
        dispatch(openInvoices())
      },
      onError: (err) => {
        showError(
          "An error occurred while attempting to submit this invoice: " +
          err.name +
          " " +
          err.message,
        )
      },
    })
  }

  function buildSaveObject(mode: "save" | "submit" | "skipValidations") {
    // build the save object
    const invoiceLines = enteredInvoiceInformation.lineItems
      .filter((line) =>
        enteredInvoiceInformation.rowSelectionModel.includes(
          line.temp_line_identifier,
        ),
      )
      .map((line) => ({
        ...line,
        line_type: line.non_editable_po_fields?.line_type,
        code_combination: line.non_editable_po_fields?.distribution_code,
        shipment_number: line.non_editable_po_fields?.shipment_number,
      }))

    return {
      submit: mode === "submit",
      skipValidation: mode === "skipValidations",
      invoiceId: enteredInvoiceInformation.invoiceId,
      glDate: convertDateToYyyyMmDd(enteredInvoiceInformation.glDate),
      noteText: enteredInvoiceInformation.note,
      invoice_user_flag: enteredInvoiceInformation.invoice_user_flag,
      currency: enteredInvoiceInformation.currency,
      invoiceDate: convertDateToYyyyMmDd(enteredInvoiceInformation.invoiceDate),
      externalInvoiceId: enteredInvoiceInformation.externalInvoiceId,
      receiverNameInvoice: enteredInvoiceInformation.receiverNameInvoice,
      netAmount: enteredInvoiceInformation.totalLineAmount,
      totalAmount: enteredInvoiceInformation.grossAmount,
      totalTaxAmount: enteredInvoiceInformation.taxAmount,
      supplierSiteId: enteredInvoiceInformation.supplierSiteId
        ? enteredInvoiceInformation.supplierSiteId
        : null,
      invoiceLinesItems: invoiceLines,
      invoiceSupplierName: enteredInvoiceInformation.invoiceSupplierName,
      invoiceType: enteredInvoiceInformation.invoiceType,
      supplierBankDetailsId: enteredInvoiceInformation.supplierBankDetailsId
        ? enteredInvoiceInformation.supplierBankDetailsId
        : null,
      supplierIban: enteredInvoiceInformation.supplierIban,
      supplierBicSwift: enteredInvoiceInformation.supplierBicSwift,
      supplierBankAccountNumber: enteredInvoiceInformation.supplierBankAccountNumber,
      supplierSortCode: enteredInvoiceInformation.supplierSortCode,
      qrIban: enteredInvoiceInformation.qrIban,
      qrIbanReference: enteredInvoiceInformation.qrIbanReference,
      isQrr: enteredInvoiceInformation.qrIban || enteredInvoiceInformation.qrIbanReference ? true : false,
      spotRates: enteredInvoiceInformation.spotRates ? enteredInvoiceInformation.spotRates : null,
      nonPoRequester: enteredInvoiceInformation.nonPoRequester
        ? enteredInvoiceInformation.nonPoRequester
        : null,
    } as SaveInvoiceArgs
  }

  return (
    <Bar>
      <PriButton
        variant="outlined"
        color= "secondary"
        startIcon={<ArrowBackOutlined />}
        onClick={() => dispatch(openInvoices())}
        sx={{ pl: 0, fontWeight: '600'}}
      >
        Back
      </PriButton>

      <Spacer />
      <AssignedToLabel>Assigned to:</AssignedToLabel>
      <AssignedToName>
        {breakStringIntoLines(enteredInvoiceInformation.assignedUser, 35).map(
          (elem: string) => (
            <AssignedToSlugs>{elem}</AssignedToSlugs>
          ),
        )}
      </AssignedToName>
      <Separator />
      <PriButton
        variant="outlined"
        color="secondary"
        onClick={() => setUnassignModalOpen(true)}
        disabled={LGInternalUser}
      >
        Unassign
      </PriButton>
      <PriButton
        variant="outlined"
        color="secondary"
        onClick={() => setMoveModalOpen(true)}
        disabled={LGInternalUser}
      >
        Move to
      </PriButton>
      <PriButton
        variant="outlined"
        color="secondary"
        disabled={!enableSaveAndSubmit || LGInternalUser}
        onClick={() => setRejectModalOpen(true)}
      >
        Reject invoice
      </PriButton>
      <Separator />
      <PriButton
        variant="outlined"
        onClick={handleSave}
        disabled={!enableSaveAndSubmit || LGInternalUser}
        sx={{height: '38px', marginRight: 1,}}
      >
        Save
      </PriButton>
      <PriButton
        variant="outlined"
        onClick={() => setSkipValidationModalOpen(true)}
        disabled={!enableSaveAndSubmit || LGInternalUser}
        sx={{height: '38px', marginRight: 1,}}
      >
        Skip validation
      </PriButton>
      <PriButton
        variant="outlined"
        onClick={handleSubmit}
        disabled={!enableSaveAndSubmit || LGInternalUser}
        sx={{height: '38px'}}
      >
        Complete validation
      </PriButton>

      <UnassignModal
        open={unassignModalOpen}
        onClose={() => setUnassignModalOpen(false)}
        invoiceIds={[invoiceId!]}
      />
      <MoveInvoiceModal
        open={moveModalOpen}
        onClose={() => setMoveModalOpen(false)}
        invoiceIds={[invoiceId!]}
      />
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        invoiceIds={[invoiceId!]}
      />
      <SkipValidationModal
        open={skipValidationModalOpen}
        onClose={() => setSkipValidationModalOpen(false)}
        onSubmit={handleSkipValidation}
      />
    </Bar>
  )
}
